/**
 * Response Comparison View - CR-1403
 *
 * Side-by-side comparison of supplier responses:
 * - Line item price comparison
 * - Highlight best prices
 * - Total cost comparison
 * - Award button per supplier
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Award, Trophy, TrendingDown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQ, RFQComparison, SupplierSummary, LineComparison } from '../../lib/api';
import AwardDialog from './AwardDialog';
import { useFormatters } from '../../contexts/PreferencesContext';

interface ResponseComparisonProps {
  rfq: RFQ;
  onAward?: () => void;
}

export default function ResponseComparison({ rfq, onAward }: ResponseComparisonProps) {
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState<RFQComparison | null>(null);
  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierSummary | null>(null);

  useEffect(() => {
    fetchComparison();
  }, [rfq.id]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const response = await rfqAPI.compareResponses(rfq.id);
      setComparison(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
      toast.error('Failed to load response comparison');
    } finally {
      setLoading(false);
    }
  };

  const handleAwardClick = (supplier: SupplierSummary) => {
    setSelectedSupplier(supplier);
    setShowAwardDialog(true);
  };

  const handleAwardComplete = () => {
    setShowAwardDialog(false);
    setSelectedSupplier(null);
    onAward?.();
  };

  const { formatCurrency: contextFormatCurrency } = useFormatters();

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return contextFormatCurrency(num, rfq.currency, true);
  };

  const getBestPrice = (lineComparison: LineComparison): number | null => {
    const prices = lineComparison.supplier_quotes
      .map(q => parseFloat(q.unit_price))
      .filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const getLowestTotalSupplier = (): number | null => {
    if (!comparison?.suppliers.length) return null;
    let lowest = comparison.suppliers[0];
    for (const supplier of comparison.suppliers) {
      if (parseFloat(supplier.grand_total) < parseFloat(lowest.grand_total)) {
        lowest = supplier;
      }
    }
    return lowest.id;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!comparison || comparison.suppliers.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">No supplier responses to compare yet.</p>
        </CardContent>
      </Card>
    );
  }

  const lowestTotalSupplierId = getLowestTotalSupplier();

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {comparison.suppliers.map((supplier) => {
          const isLowest = supplier.id === lowestTotalSupplierId;
          return (
            <Card key={supplier.id} className={isLowest ? 'border-green-500 border-2' : ''}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{supplier.name}</CardTitle>
                  {isLowest && (
                    <Badge className="bg-green-500">
                      <Trophy className="h-3 w-3 mr-1" /> Lowest
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Line Items:</span>
                    <span>{formatCurrency(supplier.total_quoted)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Additional Costs:</span>
                    <span>{formatCurrency(supplier.additional_costs)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-1 border-t">
                    <span>Total:</span>
                    <span className={isLowest ? 'text-green-600' : ''}>
                      {formatCurrency(supplier.grand_total)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{supplier.lead_time_days} days</span>
                  </div>
                  <div>
                    <span>Valid: {supplier.validity_days} days</span>
                  </div>
                </div>

                {(rfq.status === 'sent' || rfq.status === 'submitted') && (
                  <Button
                    className="w-full"
                    variant={isLowest ? 'default' : 'outline'}
                    onClick={() => handleAwardClick(supplier)}
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Award to {supplier.name}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Line Item Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Line Item Comparison</CardTitle>
          <CardDescription>Unit prices by line item across all suppliers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Line</th>
                  <th className="text-left p-3 font-medium">Description</th>
                  <th className="text-right p-3 font-medium">Qty</th>
                  {comparison.suppliers.map(supplier => (
                    <th key={supplier.id} className="text-right p-3 font-medium min-w-[120px]">
                      {supplier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.line_comparisons.map((line) => {
                  const bestPrice = getBestPrice(line);
                  return (
                    <tr key={line.line_number} className="border-b hover:bg-muted/50">
                      <td className="p-3">{line.line_number}</td>
                      <td className="p-3">
                        <div className="font-medium">{line.item_description}</div>
                        <div className="text-xs text-muted-foreground">{line.part_number}</div>
                      </td>
                      <td className="p-3 text-right">
                        {line.quantity.toLocaleString()} {line.unit_of_measure}
                      </td>
                      {comparison.suppliers.map(supplier => {
                        const quote = line.supplier_quotes.find(q => q.supplier_id === supplier.id);
                        const unitPrice = quote ? parseFloat(quote.unit_price) : 0;
                        const isBest = unitPrice > 0 && unitPrice === bestPrice;

                        return (
                          <td key={supplier.id} className="p-3 text-right">
                            {quote && unitPrice > 0 ? (
                              <div className={isBest ? 'font-bold text-green-600' : ''}>
                                <div className="flex items-center justify-end gap-1">
                                  {isBest && <TrendingDown className="h-3 w-3" />}
                                  {formatCurrency(quote.unit_price)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Total: {formatCurrency(quote.total_price)}
                                </div>
                                {quote.lead_time_days && (
                                  <div className="text-xs text-muted-foreground">
                                    {quote.lead_time_days} days
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/50 font-medium">
                <tr>
                  <td colSpan={3} className="p-3 text-right">Line Items Total:</td>
                  {comparison.suppliers.map(supplier => (
                    <td key={supplier.id} className="p-3 text-right">
                      {formatCurrency(supplier.total_quoted)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={3} className="p-3 text-right">Additional Costs:</td>
                  {comparison.suppliers.map(supplier => (
                    <td key={supplier.id} className="p-3 text-right">
                      {formatCurrency(supplier.additional_costs)}
                    </td>
                  ))}
                </tr>
                <tr className="text-lg">
                  <td colSpan={3} className="p-3 text-right font-bold">Grand Total:</td>
                  {comparison.suppliers.map(supplier => {
                    const isLowest = supplier.id === lowestTotalSupplierId;
                    return (
                      <td
                        key={supplier.id}
                        className={`p-3 text-right font-bold ${isLowest ? 'text-green-600' : ''}`}
                      >
                        {formatCurrency(supplier.grand_total)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Costs Breakdown */}
      {Object.keys(comparison.additional_cost_comparison).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Costs Breakdown</CardTitle>
            <CardDescription>Comparison of additional costs by type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Cost Type</th>
                    {comparison.suppliers.map(supplier => (
                      <th key={supplier.id} className="text-right p-3 font-medium">
                        {supplier.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparison.additional_cost_comparison).map(([costType, supplierCosts]) => (
                    <tr key={costType} className="border-b hover:bg-muted/50">
                      <td className="p-3 capitalize">{costType.replace('_', ' ')}</td>
                      {comparison.suppliers.map(supplier => (
                        <td key={supplier.id} className="p-3 text-right">
                          {supplierCosts[supplier.id]
                            ? formatCurrency(supplierCosts[supplier.id])
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Award Dialog */}
      {showAwardDialog && selectedSupplier && (
        <AwardDialog
          rfq={rfq}
          supplier={selectedSupplier}
          onClose={() => setShowAwardDialog(false)}
          onAward={handleAwardComplete}
        />
      )}
    </div>
  );
}
